import { useCallback, useEffect, useState } from 'react';
import { Section } from '@/components/Section';
import { startTrialCheckout, type WaiverKind } from '@/lib/api';
import { events, track } from '@/lib/analytics';
import { CheckoutStepper } from './join/Stepper';
import { LoginStep, type AuthSession } from './join/LoginStep';
import { loadAuthSession } from './join/authSessionStorage';
import { formatPhoneInput, type SignedSet, type WaiverPersonalInfo } from './join/WaiversStep';
import { type IdentityStatus } from './join/IdentityStep';
import { WaiverAndIdentityStep } from './join/WaiverAndIdentityStep';
import { TrialDetailsStep, summarySlot, type TrialSlotPick } from './trial/TrialDetailsStep';

const TRIAL_STEP_VIEW_EVENTS = [
  events.trialSignupStep1,
  events.trialSignupStep2,
  events.trialSignupStep3,
  events.trialSignupStep4,
] as const;

export function TrialPage() {
  const [initialSession] = useState<AuthSession | null>(() => loadAuthSession());
  const [pickedSlot, setPickedSlot] = useState<TrialSlotPick | null>(null);
  const [under18, setUnder18] = useState(false);
  const isMinor = under18 || (pickedSlot?.isKidsClass ?? false);
  const [parentEmail, setParentEmail] = useState('');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(initialSession?.email ?? '');
  const [session, setSession] = useState<AuthSession | null>(initialSession);

  const [signed, setSigned] = useState<SignedSet>({});
  const [personalInfo, setPersonalInfo] = useState<WaiverPersonalInfo>({
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    dateOfBirth: '',
    medicalConditions: '',
  });
  const [identityStatus, setIdentityStatus] = useState<IdentityStatus>('unverified');
  useEffect(() => {
    if (personalInfo.phone && personalInfo.phone !== phone) setPhone(personalInfo.phone);
  }, [personalInfo.phone, phone]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    track(events.trialStart);
  }, []);

  const requiredWaivers: WaiverKind[] = isMinor ? ['LIABILITY_MINOR'] : ['LIABILITY_ADULT'];

  const trackTrialStepView = useCallback(
    (_step: unknown, index: number) => {
      const event = TRIAL_STEP_VIEW_EVENTS[index];
      if (!event) return;
      track(event, {
        funnel: 'trial_signup',
        step: index + 1,
        classChoice: pickedSlot?.classChoice,
        dayChoice: pickedSlot?.dayChoice,
        slotId: pickedSlot?.slotId,
        isMinor,
      });
    },
    [isMinor, pickedSlot]
  );

  async function submitCheckout() {
    if (!session || !pickedSlot) return;
    setSubmitting(true);
    setSubmitError(null);
    track(events.trialCheckoutSubmit, {
      classChoice: pickedSlot.classChoice,
      dayChoice: pickedSlot.dayChoice,
      slotId: pickedSlot.slotId,
      isMinor,
    });
    try {
      const { url } = await startTrialCheckout({
        name,
        email: session.email,
        phone,
        classChoice: pickedSlot.classChoice,
        dayChoice: pickedSlot.dayChoice,
        classDate: pickedSlot.classDate,
        slotId: pickedSlot.slotId,
        slotTitle: pickedSlot.slotTitle,
        slotStartTime: pickedSlot.startTime,
        slotEndTime: pickedSlot.endTime,
        isMinor,
        parentEmail: isMinor ? parentEmail : undefined,
        authToken: session.authToken,
      });
      window.location.href = url;
    } catch (err) {
      const msg = String((err as Error).message ?? err);
      track(events.trialCheckoutError, { error: msg });
      setSubmitError('Something went wrong. Please try again or call (972) 977-5605.');
      setSubmitting(false);
    }
  }

  return (
    <Section eyebrow="$20 Trial" title="Book your trial class">
      <p className="mb-8 max-w-2xl text-bone/70">
        Pay $20 to lock your spot. You&apos;ll sign a liability waiver and verify your ID before
        checkout. Confirmation sent to your email.
      </p>
      <CheckoutStepper
        onComplete={() => void submitCheckout()}
        onStepView={trackTrialStepView}
        steps={[
          {
            id: 'details',
            title: '1. Pick a class day & time',
            body: () => (
              <TrialDetailsStep
                pickedSlot={pickedSlot}
                setPickedSlot={setPickedSlot}
                under18={under18}
                setUnder18={setUnder18}
              />
            ),
            summary: () =>
              pickedSlot ? `${summarySlot(pickedSlot)}${isMinor ? ' · minor' : ''}` : null,
            validate: () =>
              pickedSlot ? null : { message: 'Pick a class day and time to continue.' },
          },
          {
            id: 'login',
            title: '2. Sign-In',
            hideContinue: true,
            body: ({ next }) => (
              <LoginStep
                email={email}
                setEmail={setEmail}
                session={session}
                setSession={setSession}
                next={next}
              />
            ),
            summary: () => (session ? `Signed in as ${session.email}` : null),
            validate: () => (session ? null : { message: 'Sign in to continue.' }),
          },
          {
            id: 'waiver',
            title: '3. Sign Waiver',
            body: () =>
              session ? (
                <WaiverAndIdentityStep
                  required={requiredWaivers}
                  authToken={session.authToken}
                  legalName={name}
                  setLegalName={setName}
                  signed={signed}
                  setSigned={setSigned}
                  signerEmail={session.email}
                  personalInfo={personalInfo}
                  setPersonalInfo={setPersonalInfo}
                  identityStatus={identityStatus}
                  setIdentityStatus={setIdentityStatus}
                />
              ) : (
                <p className="text-sm text-bone/60">Sign in first.</p>
              ),
            summary: () => {
              const allWaiversSigned = requiredWaivers.every((k) => Boolean(signed[k]));
              if (allWaiversSigned && identityStatus === 'verified') {
                return `${requiredWaivers.length} waivers signed · ID verified`;
              }
              if (allWaiversSigned) return `${requiredWaivers.length} waivers signed`;
              return null;
            },
            validate: () => {
              if (name.length < 2) {
                return { message: 'Type your legal full name to use as your signature.' };
              }
              const missing = requiredWaivers.filter((k) => !signed[k]);
              if (missing.length > 0) {
                return {
                  message: `Please sign all required waivers (${missing.length} remaining).`,
                };
              }
              if (identityStatus !== 'verified') {
                return { message: 'Verify your government ID to continue.' };
              }
              return null;
            },
          },
          {
            id: 'confirm',
            title: '4. Confirm & pay',
            body: () => (
              <div className="grid gap-3 text-sm">
                <p>
                  Class: <strong>{pickedSlot ? summarySlot(pickedSlot) : '—'}</strong>
                </p>
                <p>
                  Email: <strong>{session?.email}</strong>
                </p>
                {phone.replace(/\D/g, '').length >= 7 ? (
                  <p>
                    Phone: <strong>{phone}</strong>
                  </p>
                ) : (
                  <label className="block">
                    <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
                      Phone
                    </span>
                    <input
                      id="field-phone"
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                      className="input"
                      autoComplete="tel"
                      placeholder="(972) 555-0123 or +44 20 7123 4567"
                    />
                  </label>
                )}
                {isMinor && (
                  <label className="block">
                    <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
                      Parent / guardian email
                    </span>
                    <input
                      type="email"
                      required
                      value={parentEmail}
                      onChange={(e) => setParentEmail(e.target.value)}
                      className="input"
                    />
                  </label>
                )}
                {submitting && <p className="text-bone/60">Redirecting to Stripe…</p>}
                {submitError && (
                  <div
                    role="alert"
                    className="rounded border border-accent/50 bg-accent/10 p-3 text-accent"
                  >
                    {submitError}
                  </div>
                )}
              </div>
            ),
            summary: () => null,
            validate: () => {
              if (phone.replace(/\D/g, '').length < 7) {
                return { message: 'Enter your phone number.', focusId: 'field-phone' };
              }
              if (isMinor && !parentEmail) return { message: 'Enter parent / guardian email.' };
              return null;
            },
          },
        ]}
      />
    </Section>
  );
}
